import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchPurchases, deletePurchase, fetchPurchaseById } from './controller/purchase-controller';
import { format, addDays, subDays, startOfMonth, endOfMonth } from 'date-fns';
import DeleteConfirmationDialog from '../../../components/DeleteConfirmationDialog';
import { toast , ToastContainer } from 'react-toastify'; // Import toast
import { usePermissionViewModel } from '../permissions/viewmodel/permission-viewmodel';
import JsBarcode from 'jsbarcode';

const PurchasesList = () => {
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [dateRange, setDateRange] = useState({
        startDate: format(startOfMonth(new Date()), 'yyyy-MM-dd\'T\'00:00'),
        endDate: format(endOfMonth(new Date()), 'yyyy-MM-dd\'T\'23:59')
    });
    const [filterVisible, setFilterVisible] = useState(false);
    const [filtersApplied, setFiltersApplied] = useState(false);

    // Delete confirmation modal state
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [purchaseToDelete, setPurchaseToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const {hasPermission} = usePermissionViewModel('achat')


    // Date presets for quick filters
    const datePresets = {
        today: {
            label: "Aujourd'hui",
            getValue: () => ({
                startDate: format(new Date().setHours(0, 0, 0, 0), 'yyyy-MM-dd\'T\'00:00'),
                endDate: format(new Date().setHours(23, 59, 59, 999), 'yyyy-MM-dd\'T\'23:59')
            })
        },
        yesterday: {
            label: "Hier",
            getValue: () => ({
                startDate: format(subDays(new Date(), 1).setHours(0, 0, 0, 0), 'yyyy-MM-dd\'T\'00:00'),
                endDate: format(subDays(new Date(), 1).setHours(23, 59, 59, 999), 'yyyy-MM-dd\'T\'23:59')
            })
        },
        last7Days: {
            label: "7 derniers jours",
            getValue: () => ({
                startDate: format(subDays(new Date(), 6).setHours(0, 0, 0, 0), 'yyyy-MM-dd\'T\'00:00'),
                endDate: format(new Date().setHours(23, 59, 59, 999), 'yyyy-MM-dd\'T\'23:59')
            })
        },
        last30Days: {
            label: "30 derniers jours",
            getValue: () => ({
                startDate: format(subDays(new Date(), 29).setHours(0, 0, 0, 0), 'yyyy-MM-dd\'T\'00:00'),
                endDate: format(new Date().setHours(23, 59, 59, 999), 'yyyy-MM-dd\'T\'23:59')
            })
        },
        thisMonth: {
            label: "Ce mois",
            getValue: () => ({
                startDate: format(startOfMonth(new Date()).setHours(0, 0, 0, 0), 'yyyy-MM-dd\'T\'00:00'),
                endDate: format(endOfMonth(new Date()).setHours(23, 59, 59, 999), 'yyyy-MM-dd\'T\'23:59')
            })
        }
    };

    useEffect(() => {
        document.title = "Achats | Administration";
    }, []);

    useEffect(() => {
        loadPurchases();
    }, [currentPage, filtersApplied]);

    const loadPurchases = async () => {
        try {
            setLoading(true);

            // Create query params
            const params = {
                page: currentPage,
                limit: pageSize
            };

            // Add search term if present
            if (searchTerm) {
                params.search = searchTerm;
            }

            // Add date range if present
            if (dateRange.startDate) {
                params.startDate = dateRange.startDate;
            }

            if (dateRange.endDate) {
                params.endDate = dateRange.endDate;
            }

            const data = await fetchPurchases(params);

            // Update this part to match your API response structure
            setPurchases(data.list || []);
            setTotalPages(data.maxPage || 1);
        } catch (err) {
            setError('Erreur lors du chargement des achats');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleDatePresetChange = (preset) => {
        setDateRange(datePresets[preset].getValue());
    };

    const handleSearch = (e) => {
        if (e.key === 'Enter') {
            applyFilters();
        }
    };

    const applyFilters = () => {
        setCurrentPage(1); // Reset to first page when applying filters
        setFiltersApplied(!filtersApplied); // Toggle to trigger useEffect
    };

    const clearFilters = () => {
        setSearchTerm('');
        setDateRange(datePresets.thisMonth.getValue());
        setFiltersApplied(!filtersApplied); // Toggle to trigger useEffect
    };

    const handleDelete = (purchase) => {
        setPurchaseToDelete(purchase);
        setDeleteModalOpen(true);
    };

    // Print one barcode label per purchased unit (quantite per line).
    // Same layout as ProductsPage: 50 x 40 mm landscape, Honeywell PC42E-T.
    const printPurchaseBarcodes = async (purchase) => {
        try {
            const data = await fetchPurchaseById(purchase.id);
            const lines = data?.line || data?.Line || [];
            if (lines.length === 0) {
                toast.warn('Aucune ligne dans cet achat');
                return;
            }
            const esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

            const ticketsHtml = [];
            for (const ln of lines) {
                const variant = ln.productVariant || ln.ProductVariant || null;
                const product = ln.product || ln.Product || null;
                const qty = Number(ln.quantite || ln.Quantite || 0);
                const code = String(variant?.codeABarre || variant?.CodeABarre || product?.codeABarre || product?.CodeABarre || '').trim();
                if (!code || qty <= 0) continue;
                const regularNum = Number(variant?.prixVenteTTC ?? product?.prixVenteTTC ?? 0);
                const enPromo = Boolean(product?.enPromo ?? product?.EnPromo) && Number(product?.prixVentePromo ?? product?.PrixVentePromo) > 0;
                const promoNum = Number(product?.prixVentePromo ?? product?.PrixVentePromo ?? 0);
                const effectiveNum = enPromo ? promoNum : regularNum;
                const priceText = Number.isFinite(effectiveNum) && effectiveNum > 0 ? `${effectiveNum.toFixed(2)} TND` : '';
                const oldText = enPromo && regularNum > effectiveNum ? `${regularNum.toFixed(2)} TND` : '';
                let dataUrl = '';
                try {
                    const canvas = document.createElement('canvas');
                    JsBarcode(canvas, code, {
                        format: 'CODE128',
                        width: 2,
                        height: 95,
                        fontSize: 20,
                        textMargin: 2,
                        fontOptions: 'bold',
                        margin: 0,
                        displayValue: true,
                        background: '#ffffff',
                        lineColor: '#000000',
                    });
                    dataUrl = canvas.toDataURL('image/png');
                } catch (e) {
                    console.error('Barcode generation failed for', code, e);
                }
                const productName = product?.name || product?.Name || '';
                const variantName = variant?.name || variant?.Name || '';
                const refText = String(variant?.reference || variant?.Reference || product?.reference || product?.Reference || '').trim();
                const ticket = `
                    <div class="ticket">
                      <div class="brand">Tendresse Store</div>
                      ${refText ? `<div class="reference">${esc(refText)}</div>` : ''}
                      ${variantName ? `<div class="variant">${esc(variantName)}</div>` : ''}
                      <div class="name">${esc(productName)}</div>
                      <div class="barcode-wrap">
                        ${dataUrl
                          ? `<img class="barcode" src="${dataUrl}" alt="${esc(code)}"/>`
                          : `<div class="barcode-fallback">${esc(code)}</div>`}
                      </div>
                      <div class="price">${oldText ? `<span class="price-old">${esc(oldText)}</span> ` : ''}${esc(priceText)}</div>
                    </div>`;
                for (let i = 0; i < qty; i++) ticketsHtml.push(ticket);
            }

            if (ticketsHtml.length === 0) {
                toast.warn('Aucun code-barres disponible pour cet achat');
                return;
            }

            const printHtml = `<!doctype html><html><head><meta charset="utf-8"/>
              <title>Codes-barres - ${esc(purchase.reference || '')}</title>
              <style>
                @page { size: 50mm 40mm; margin: 0; }
                html, body { margin: 0; padding: 0; background: #fff; }
                body { font-family: Arial, 'Helvetica', sans-serif; color: #000;
                       -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                .ticket {
                  width: 50mm; height: 40mm;
                  padding: 1.5mm 2mm 1.5mm 2mm;
                  box-sizing: border-box;
                  display: flex; flex-direction: column;
                  align-items: center; justify-content: flex-start;
                  page-break-after: always; break-after: page;
                  overflow: hidden;
                }
                .ticket:last-child { page-break-after: auto; break-after: auto; }
                .brand { font-size: 11pt; font-weight: 900; text-align: center;
                         line-height: 1; letter-spacing: 1.2px;
                         font-family: 'Georgia', 'Times New Roman', serif;
                         font-style: italic;
                         width: 100%;
                         padding-bottom: 0.6mm;
                         border-bottom: 0.3mm solid #000; }
                .name { font-size: 6.5pt; font-weight: 700; text-align: center;
                        line-height: 1.05; margin-top: 0.3mm; max-height: 4mm; overflow: hidden;
                        word-break: break-word; }
                .variant { font-size: 8.5pt; font-weight: 900; text-align: center;
                           line-height: 1.05; margin-top: 0.4mm; overflow: visible;
                           word-break: break-word; }
                .reference { font-size: 9.5pt; font-weight: 900; text-align: center;
                             line-height: 1.05; margin-top: 0.6mm; color: #000;
                             overflow: visible; word-break: break-word; }
                .barcode-wrap { width: 100%; display: flex; justify-content: center;
                                align-items: center; margin-top: 0.8mm; }
                .barcode { width: 46mm; height: 17mm; display: block; }
                .barcode-fallback { font-family: 'Courier New', monospace;
                                    font-size: 11pt; font-weight: 700; }
                .price { font-size: 12pt; font-weight: 900; text-align: center;
                         margin-top: 1mm; letter-spacing: 0.3px; white-space: nowrap; }
                .price-old { font-size: 8pt; font-weight: 700; text-decoration: line-through;
                             color: #555; margin-right: 1mm; }
              </style></head><body>
              ${ticketsHtml.join('')}
              </body></html>`;

            // Print via a hidden iframe (more reliable than window.open, which can
            // leave a blank about:blank popup if the browser blocks document.write).
            const iframe = document.createElement('iframe');
            iframe.style.position = 'fixed';
            iframe.style.right = '0';
            iframe.style.bottom = '0';
            iframe.style.width = '0';
            iframe.style.height = '0';
            iframe.style.border = '0';
            iframe.setAttribute('aria-hidden', 'true');
            document.body.appendChild(iframe);
            const cleanup = () => { try { document.body.removeChild(iframe); } catch (e) { /* ignore */ } };
            iframe.onload = () => {
              try {
                const w = iframe.contentWindow;
                const imgs = Array.prototype.slice.call(w.document.images);
                const triggerPrint = () => {
                  try { w.focus(); w.print(); } catch (e) { console.error(e); }
                  w.onafterprint = () => setTimeout(cleanup, 200);
                  setTimeout(cleanup, 60000);
                };
                let done = 0;
                const check = () => { if (done >= imgs.length) setTimeout(triggerPrint, 150); };
                if (imgs.length === 0) { setTimeout(triggerPrint, 150); return; }
                imgs.forEach((im) => {
                  if (im.complete && im.naturalWidth > 0) { done++; }
                  else {
                    im.addEventListener('load', () => { done++; check(); });
                    im.addEventListener('error', () => { done++; check(); });
                  }
                });
                check();
              } catch (e) { console.error(e); cleanup(); }
            };
            const doc = iframe.contentDocument || iframe.contentWindow.document;
            doc.open();
            doc.write(printHtml);
            doc.close();
        } catch (err) {
            console.error('printPurchaseBarcodes error', err);
            toast.error("Erreur lors de la génération des codes-barres");
        }
    };

    const confirmDelete = async () => {
        if (!purchaseToDelete) return;

        try {
            setIsDeleting(true);
            await deletePurchase(purchaseToDelete.id);

            // Show success toast notification
            toast.success(`L'achat ${purchaseToDelete.reference} a été supprimé avec succès`);

            // Reload purchases and reset state
            loadPurchases();
            setDeleteModalOpen(false);
            setPurchaseToDelete(null);
        } catch (err) {
            // Show error toast notification
            toast.error(`Erreur lors de la suppression: ${err.response.data.errorMessage || 'Veuillez réessayer'}`);
                

            setError('Erreur lors de la suppression de l\'achat');
            console.error(err);
        } finally {
            setIsDeleting(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    };

    return (
        <div className="px-4 sm:px-6 lg:px-8 py-8 bg-gray-50 min-h-screen">
            <div className="sm:flex sm:items-center sm:justify-between mb-8">
                <div>
                    <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Achats</h1>
                    <p className="mt-2 text-sm text-gray-700">
                        Liste de tous les achats
                    </p>
                </div>
{        hasPermission('write')     &&   <Link
                    to="/admin/purchases/new"
                    className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
                >
                    <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Nouveau Achat
                </Link>
}            </div>

            {/* Search and filters */}
            <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                    <div className="relative flex-grow">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={handleSearch}
                            placeholder="Rechercher par référence ou fournisseur..."
                            className="pl-10 py-2 block w-full shadow-sm focus:ring-[#174540] focus:border-[#174540] sm:text-sm border-gray-300 rounded-md"
                        />
                    </div>
                    <button
                        onClick={applyFilters}
                        className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
                    >
                        Appliquer
                    </button>
                    <button
                        onClick={clearFilters}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
                    >
                        Réinitialiser
                    </button>
                </div>

                {/* Date filter options - always visible */}
                <div className="mt-3 p-6 bg-white rounded-lg border border-gray-100 transition-all duration-300 ease-in-out">
                    <div className="mb-5">
                        <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-[#174540]" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                            </svg>
                            Période
                        </h3>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                            {Object.keys(datePresets).map((key) => (
                                <button
                                    key={key}
                                    onClick={() => handleDatePresetChange(key)}
                                    className={`
                                        px-3 py-2 text-xs font-medium rounded-md transition-all duration-200 ease-in-out
                                        flex items-center justify-center
                                        ${dateRange.startDate === datePresets[key].getValue().startDate &&
                                            dateRange.endDate === datePresets[key].getValue().endDate
                                            ? 'bg-[#174540] text-white transform scale-105'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:transform hover:scale-102'
                                        }
                                    `}
                                >
                                    {key === 'today' && (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                    {key === 'yesterday' && (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                            <path d="M5.394 8.795a.75.75 0 010-1.59h7.74a.75.75 0 010 1.59H5.394zM4.044 2.442a1 1 0 01-.868 1.116A9.981 9.981 0 001.553 10c0 5.523 4.477 10 10 10s10-4.477 10-10S17.076 0 11.553 0h-.006a1 1 0 01-1 1l-6.5 1.442z" />
                                        </svg>
                                    )}
                                    {key === 'last7Days' && (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-14a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 7.586V4z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                    {datePresets[key].label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className="relative group">
                            <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-[#174540]" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                </svg>
                                Date et heure de début
                            </label>
                            <div className="relative">
                                <input
                                    type="datetime-local"
                                    id="start-date"
                                    value={dateRange.startDate}
                                    onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                                    className="block w-full pl-3 pr-10 py-2.5 focus:ring-[#174540] focus:border-[#174540] sm:text-sm border-2 border-gray-300 rounded-md transition-all duration-200 ease-in-out group-hover:border-gray-400"
                                />
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 group-hover:text-[#174540] transition-colors duration-200" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                        <div className="relative group">
                            <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-[#174540]" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                </svg>
                                Date et heure de fin
                            </label>
                            <div className="relative">
                                <input
                                    type="datetime-local"
                                    id="end-date"
                                    value={dateRange.endDate}
                                    onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                                    className="block w-full pl-3 pr-10 py-2.5 focus:ring-[#174540] focus:border-[#174540] sm:text-sm border-2 border-gray-300 rounded-md transition-all duration-200 ease-in-out group-hover:border-gray-400"
                                />
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 group-hover:text-[#174540] transition-colors duration-200" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mt-5 flex justify-end">
                        <button
                            onClick={applyFilters}
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] transition-all duration-200 ease-in-out transform hover:scale-105"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Appliquer les filtres
                        </button>
                    </div>
                </div>
            </div>

            {error && (
                <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-400 text-red-700">
                    <p>{error}</p>
                </div>
            )}

            {loading ? (
                <div className="flex justify-center items-center py-16">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#174540]"></div>
                </div>
            ) : purchases.length === 0 ? (
                <div className="bg-white shadow rounded-lg p-10 text-center">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <h3 className="mt-2 text-lg font-medium text-gray-900">Aucun achat trouvé</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        {searchTerm || (dateRange.startDate !== datePresets.thisMonth.getValue().startDate) ?
                            'Aucun résultat ne correspond à vos critères de recherche.' :
                            'Commencez par créer un nouvel achat.'}
                    </p>
                    <div className="mt-6">
                        {searchTerm || (dateRange.startDate !== datePresets.thisMonth.getValue().startDate) ? (
                            <button
                                onClick={clearFilters}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
                            >
                                Réinitialiser les filtres
                            </button>
                        ) : (
                            <>
{            hasPermission("write")&&                <Link
                                to="/admin/purchases/new"
                                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
                            >
                                <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                                </svg>
                                Créer un achat
                            </Link>
}          </>              )}
                    </div>
                </div>
            ) : (
                <>
                    <div className="mt-8 flex flex-col">
                        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                                    <table className="min-w-full divide-y divide-gray-300">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Référence</th>
                                                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Fournisseur</th>
                                                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Date</th>
                                                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Total TTC</th>
                                                <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                                                    <span className="sr-only">Actions</span>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white">
                                            {purchases.map((purchase) => (
                                                <tr key={purchase.id}>
                                                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                                        {purchase.reference}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                        {purchase.fournisseur?.name || purchase.fournisseurName || '-'}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                        {formatDate(purchase.dateBonCommande || purchase.dateBonAchat)}
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                        {purchase.totalTTC?.toFixed(2) || '0.00'} TND
                                                    </td>
                                                    <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                                        <div className="flex justify-end space-x-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => printPurchaseBarcodes(purchase)}
                                                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-[#174540] hover:bg-[#0d2926] rounded-md"
                                                                title="Imprimer tous les codes-barres de l'achat"
                                                            >
                                                                <svg className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h1m3 0h1m3 0h1m3 0h1m3 0h1M4 18h1m3 0h1m3 0h1m3 0h1m3 0h1M4 6v12M9 6v12M14 6v12M19 6v12" />
                                                                </svg>
                                                                Codes-barres
                                                            </button>
{                             hasPermission("edit") &&                               <Link
                                                                to={`/admin/purchases/${purchase.id}`}
                                                                className="text-[#174540] hover:text-[#0d2926]"
                                                            >
                                                                Modifier
                                                            </Link>
} 
                                                           <button
                                                           disabled={!hasPermission('delete')}
                                                                onClick={() => handleDelete(purchase)}
                                                                className="text-red-600 hover:text-red-900  disabled:text-gray-700  disabled:cursor-not-allowed"
                                                            >
                                                                Supprimer
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Pagination */}
                    {totalPages > 0 && (
                        <div className="mt-5 flex items-center justify-between">
                            <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }} className="border border-gray-300 rounded-md text-sm py-1 pl-2 pr-7 focus:ring-[#174540] focus:border-[#174540]">
                                {[5, 10, 25, 50].map(s => <option key={s} value={s}>{s} / page</option>)}
                            </select>
                            <nav className="flex items-center space-x-1">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className={`px-3 py-1 rounded-md ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                                >
                                    Précédent
                                </button>

                                {[...Array(totalPages)].map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setCurrentPage(i + 1)}
                                        className={`px-3 py-1 rounded-md ${currentPage === i + 1 ? 'bg-[#174540] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                                    >
                                        {i + 1}
                                    </button>
                                ))}

                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className={`px-3 py-1 rounded-md ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                                >
                                    Suivant
                                </button>
                            </nav>
                        </div>
                    )}
                </>
            )}
            <DeleteConfirmationDialog
                isOpen={deleteModalOpen}
                onClose={() => {
                    setDeleteModalOpen(false);
                    setPurchaseToDelete(null);
                }}
                onConfirm={confirmDelete}
                title="Supprimer l'achat"
                itemName={purchaseToDelete?.reference}
                itemType="achat"
                isDeleting={isDeleting}
            />

        </div>
    );
};

export default PurchasesList;